import Vue from 'vue';
import {
  apiGetChatMessages,
  apiSendChatMessage,
  apiGetPolls,
  apiCreatePoll,
  apiClosePoll,
  apiRespondToPoll,
  apiGetPollResults,
  apiLogWebinarAttendance,
} from '@/api/videos';

/* eslint-disable @typescript-eslint/no-shadow */

// ── localStorage helpers for read-state persistence ─────────────────────────
function readKey(webinarId, userId) {
  return `gf_wc_read_${webinarId}_${userId}`;
}

function getStoredLastRead(webinarId, userId) {
  try {
    return parseInt(localStorage.getItem(readKey(webinarId, userId)) || '0', 10);
  } catch {
    return 0;
  }
}

function storeLastRead(webinarId, userId, messageId) {
  try {
    if (messageId) {
      localStorage.setItem(readKey(webinarId, userId), String(messageId));
    }
  } catch {
    // Private browsing or quota exceeded — silently ignore
  }
}

const state = () => ({
  organizerMessages: [],
  privateMessages: [],
  privateThreads: {}, // organizer view: { [userId]: [messages] }
  polls: [],
  activeTab: 'private', // 'organizer' | 'private' | 'polls'
  isLoading: false,
  currentWebinarId: null,
  unreadCounts: { organizer: 0, private: 0, polls: 0 },
  unreadPrivateUsers: {}, // organizer view: { [userId]: count }
  activePrivateUserId: null, // which thread the organizer is currently viewing
});

const getters = {
  organizerMessages: (s) => s.organizerMessages,
  privateMessages: (s) => s.privateMessages,
  privateThreads: (s) => s.privateThreads,
  polls: (s) => s.polls,
  activePolls: (s) => s.polls.filter((p) => p.is_active),
  closedPolls: (s) => s.polls.filter((p) => !p.is_active),
  activeTab: (s) => s.activeTab,
  isLoading: (s) => s.isLoading,
  unreadCounts: (s) => s.unreadCounts,
  unreadPrivateUsers: (s) => s.unreadPrivateUsers,
  activePrivateUserId: (s) => s.activePrivateUserId,
  currentWebinarId: (s) => s.currentWebinarId,
};

const mutations = {
  setOrganizerMessages(state, messages) {
    state.organizerMessages = messages;
  },

  appendOrganizerMessage(state, message) {
    const exists = state.organizerMessages.some((m) => m.id === message.id);
    if (!exists) {
      state.organizerMessages.push(message);
    }
  },

  setPrivateMessages(state, messages) {
    state.privateMessages = messages;

    // Rebuild organizer threads view from loaded messages
    const threads = {};
    messages.forEach((msg) => {
      const threadKey = msg.is_from_moderator
        ? String(msg.recipient_id)
        : String(msg.sender?.id);
      if (!threadKey) return;
      if (!threads[threadKey]) threads[threadKey] = [];
      threads[threadKey].push(msg);
    });
    state.privateThreads = threads;

    // Restore per-user unread counts from localStorage based on last-read message IDs
    const unread = {};
    Object.keys(threads).forEach((userIdKey) => {
      const thread = threads[userIdKey];
      const lastRead = getStoredLastRead(state.currentWebinarId, userIdKey);
      const count = thread.filter((m) => !m.is_from_moderator && m.id > lastRead).length;
      if (count > 0) {
        unread[userIdKey] = count;
      }
    });
    state.unreadPrivateUsers = unread;
    // Reflect total in the tab-level badge
    state.unreadCounts.private = Object.values(unread).reduce((s, n) => s + n, 0);
  },

  appendPrivateMessage(state, message) {
    const exists = state.privateMessages.some((m) => m.id === message.id);
    if (!exists) {
      state.privateMessages.push(message);
    }

    // Also update the organizer threads view (Vue.set for reactivity on new keys)
    const threadKey = message.is_from_moderator ? message.recipient_id : message.sender?.id;
    if (!threadKey) return;

    const userIdKey = String(threadKey);
    if (!state.privateThreads[userIdKey]) {
      Vue.set(state.privateThreads, userIdKey, []);
    }
    const threadExists = state.privateThreads[userIdKey].some((m) => m.id === message.id);
    if (!threadExists) {
      state.privateThreads[userIdKey].push(message);
    }

    // Track per-user unread count for organizer view: only count incoming messages
    // (not sent by the moderator) from users that aren't the currently viewed thread
    if (!message.is_from_moderator) {
      if (state.activePrivateUserId !== userIdKey) {
        const current = state.unreadPrivateUsers[userIdKey] || 0;
        Vue.set(state.unreadPrivateUsers, userIdKey, current + 1);
      } else {
        // Thread is open — auto-advance the last-read pointer so reload won't show it as unread
        storeLastRead(state.currentWebinarId, userIdKey, message.id);
      }
    }
  },

  setPolls(state, polls) {
    state.polls = polls;
  },

  appendPoll(state, poll) {
    const exists = state.polls.some((p) => p.id === poll.id);
    if (!exists) {
      state.polls.push(poll);
    }
  },

  closePollById(state, { poll_id, results }) {
    const poll = state.polls.find((p) => p.id === poll_id);
    if (!poll) return;
    Vue.set(poll, 'is_active', false);
    if (results) {
      Vue.set(poll, 'results', results);
    }
  },

  updatePollResults(state, { poll_id, results }) {
    const poll = state.polls.find((p) => p.id === poll_id);
    if (poll) {
      Vue.set(poll, 'results', results);
    }
  },

  setActiveTab(state, tab) {
    state.activeTab = tab;
    if (Object.prototype.hasOwnProperty.call(state.unreadCounts, tab)) {
      state.unreadCounts[tab] = 0;
    }
  },

  incrementUnread(state, tab) {
    if (state.activeTab !== tab && Object.prototype.hasOwnProperty.call(state.unreadCounts, tab)) {
      state.unreadCounts[tab] += 1;
    }
  },

  setActivePrivateUser(state, userId) {
    const userIdKey = userId ? String(userId) : null;
    state.activePrivateUserId = userIdKey;
    // Clear unread count for this user when their thread is opened
    if (userIdKey) {
      if (state.unreadPrivateUsers[userIdKey]) {
        Vue.set(state.unreadPrivateUsers, userIdKey, 0);
      }
      // Persist: advance the last-read pointer to the latest message in this thread
      const thread = state.privateThreads[userIdKey] || [];
      const lastMsg = thread[thread.length - 1];
      if (lastMsg && state.currentWebinarId) {
        storeLastRead(state.currentWebinarId, userIdKey, lastMsg.id);
      }
    }
  },

  setLoading(state, val) {
    state.isLoading = val;
  },

  setCurrentWebinarId(state, id) {
    state.currentWebinarId = id;
  },

  reset(state) {
    state.organizerMessages = [];
    state.privateMessages = [];
    state.privateThreads = {};
    state.polls = [];
    state.activeTab = 'private';
    state.isLoading = false;
    state.currentWebinarId = null;
    state.unreadCounts = { organizer: 0, private: 0, polls: 0 };
    state.unreadPrivateUsers = {};
    state.activePrivateUserId = null;
  },
};

const actions = {
  async initChat({ commit }, { webinarId, isOrganizer = false }) {
    commit('setCurrentWebinarId', webinarId);
    commit('setLoading', true);
    try {
      const [organizerRes, privateRes, pollsRes] = await Promise.all([
        isOrganizer
          ? apiGetChatMessages({ webinar_id: webinarId, category: 'organizer' })
          : Promise.resolve({ messages: [] }),
        apiGetChatMessages({ webinar_id: webinarId, category: 'private' }),
        apiGetPolls({ webinar_id: webinarId }),
        apiLogWebinarAttendance({ webinar_id: webinarId, action: 'join' }),
      ]);

      commit('setOrganizerMessages', organizerRes?.messages || []);
      commit('setPrivateMessages', privateRes?.messages || []);

      const polls = pollsRes?.polls || [];
      commit('setPolls', polls);

      // Fetch results for each poll so we know which ones the user has already voted on
      const resultPromises = polls.map((poll) => apiGetPollResults({ poll_id: poll.id })
        .then((res) => {
          if (res?.results?.length) {
            commit('updatePollResults', { poll_id: poll.id, results: res.results });
          }
        })
        .catch(() => {}));
      await Promise.all(resultPromises);
    } catch {
      // Silently fail — chat will still work via WS; REST load is best-effort
    } finally {
      commit('setLoading', false);
    }
  },

  async destroyChat({ state, commit }) {
    if (state.currentWebinarId) {
      try {
        await apiLogWebinarAttendance({ webinar_id: state.currentWebinarId, action: 'leave' });
      } catch {
        // Best-effort
      }
    }
    commit('reset');
  },

  async sendOrganizerMessage({ state }, content) {
    await apiSendChatMessage({
      webinar_id: state.currentWebinarId,
      category: 'organizer',
      content,
    });
  },

  async sendPrivateMessage({ state }, { content, recipientId }) {
    await apiSendChatMessage({
      webinar_id: state.currentWebinarId,
      category: 'private',
      content,
      recipient_id: recipientId,
    });
  },

  async createPoll({ state }, { question, pollType, allowOther, options }) {
    await apiCreatePoll({
      webinar_id: state.currentWebinarId,
      question,
      poll_type: pollType,
      allow_other: allowOther,
      options: JSON.stringify(options),
    });
  },

  async closePoll(_, pollId) {
    await apiClosePoll({ poll_id: pollId });
  },

  async respondToPoll({ dispatch }, { pollId, optionIds, otherText }) {
    await apiRespondToPoll({
      poll_id: pollId,
      option_ids: JSON.stringify(optionIds),
      other_text: otherText || '',
    });
    // Fetch updated results so the voter can see them immediately
    await dispatch('fetchPollResults', pollId);
  },

  async fetchPollResults({ commit }, pollId) {
    const res = await apiGetPollResults({ poll_id: pollId });
    if (res?.results) {
      commit('updatePollResults', { poll_id: pollId, results: res.results });
    }
  },

  // ── WebSocket incoming message handlers ──────────────────────────────────

  onOrganizerMessage({ commit, state }, message) {
    if (message._webinar_id !== state.currentWebinarId) return; // eslint-disable-line no-underscore-dangle
    commit('appendOrganizerMessage', message);
    commit('incrementUnread', 'organizer');
  },

  onPrivateMessage({ commit, state }, message) {
    if (message._webinar_id !== state.currentWebinarId) return; // eslint-disable-line no-underscore-dangle
    commit('appendPrivateMessage', message);
    commit('incrementUnread', 'private');
  },

  onPollCreated({ commit, state }, message) {
    if (message._webinar_id !== state.currentWebinarId) return; // eslint-disable-line no-underscore-dangle
    commit('appendPoll', message);
    commit('incrementUnread', 'polls');
  },

  onPollClosed({ commit, state }, message) {
    if (message._webinar_id !== state.currentWebinarId) return; // eslint-disable-line no-underscore-dangle
    commit('closePollById', { poll_id: message.poll_id, results: message.results });
  },

  onPollResultsUpdate({ commit, state }, message) {
    if (message._webinar_id !== state.currentWebinarId) return; // eslint-disable-line no-underscore-dangle
    commit('updatePollResults', { poll_id: message.poll_id, results: message.results });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
